import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { contexts } from 'lib_ui-primitives';
import { http, eventSink } from 'lib_ui-services';
import useEventSink from '../../../hooks/useEventSink';
import { eventCard, captureCards } from './readerRecords';

const MAX_MESSAGES = 500;
const defaultSubscribe = () => eventSink()[0];

export default function ReaderEventsSource({
    record,
    visible,
    api = http,
    subscribeToEvents = defaultSubscribe,
    children
}) {
    const session = useContext(contexts.SessionContext) || {};
    const deviceId = record._id;
    const tenantId = session.tenant?.['identity:tenant']?._id;
    const base = `/g/${session.groupNumber || 0}/api/deviceManagement/device/${record._id}/diagnostics`;
    const scope = `${session.groupNumber || 0}:${session.tenant?.['identity:tenant']?._id}:${record._id}`;
    const [state, setState] = useState({ scope, messages: [], discarded: 0 });
    const [page, setPage] = useState({ cursors: [null], index: 0, next: null, loading: true });
    const [capture, setCapture] = useState(null);
    const [observation, setObservation] = useState(null);
    const sink = useEventSink(true);
    const subscribe = sink?.[0];
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const lifecycle = useRef(null);
    const observationRef = useRef(null);
    const data = state.scope === scope ? state : { messages: [], discarded: 0 };

    useEffect(() => {
        if (!visible) return;
        const current = { active: true };
        lifecycle.current = current;
        setObservation(null);
        setBusy(false);
        setError('');
        setNotice('Saved events cover the last 24 hours, 50 per page. Tag traffic is live only.');
        setState({ scope, messages: [], discarded: 0 });
        setPage({ cursors: [null], index: 0, next: null, loading: true });
        setCapture(null);
        current.pageIndex = 0;
        current.request = 0;
        const append = messages => {
            if (!current.active || current.pageIndex !== 0) return;
            setState(previous => {
                const unique = new Map((previous.scope === scope ? previous.messages : []).map(row => [row.id, row]));
                messages.forEach(row => unique.set(row.id, row));
                const sorted = [...unique.values()].sort((a, b) => new Date(a.at) - new Date(b.at));
                const overflow = Math.max(0, sorted.length - MAX_MESSAGES);
                return {
                    scope,
                    messages: sorted.slice(-MAX_MESSAGES),
                    discarded: (previous.discarded || 0) + overflow
                };
            });
        };
        const load = async (cursor = null, index = 0, merge = false) => {
            const request = ++current.request;
            current.pageIndex = index;
            setPage(previous => ({ ...previous, loading: true }));
            try {
                const result = await api.get(`${base}/events${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);
                if (!current.active || request !== current.request) return;
                setError('');
                if (merge && index === 0) append(result.messages);
                else setState({ scope, messages: result.messages, discarded: 0 });
                setPage(previous => ({
                    cursors: [...previous.cursors.slice(0, index), cursor],
                    index,
                    next: result.nextCursor,
                    loading: false
                }));
            } catch (err) {
                if (current.active && request === current.request) {
                    setError(err.message || 'Unable to load reader events');
                    setPage(previous => ({ ...previous, loading: false }));
                }
            }
        };
        current.load = load;
        load();
        // Poll only an accepted capture. Reading this status never sends MQTT.
        current.loadCapture = async () => {
            clearTimeout(current.captureTimer);
            if (current.capturePending) {
                current.captureRefreshQueued = true;
                return;
            }
            current.capturePending = true;
            try {
                const result = await api.get(`${base}/actions`);
                if (!current.active) return;
                setCapture(result);
                if (result.operation?.action === 'capture' && result.operation.status === 'active')
                    current.captureTimer = setTimeout(current.loadCapture, 3000);
            } catch (err) {
                if (current.active) setError(err.message || 'Unable to load filtering capture');
            } finally {
                current.capturePending = false;
                if (current.active && current.captureRefreshQueued) {
                    current.captureRefreshQueued = false;
                    current.loadCapture();
                }
            }
        };
        current.loadCapture();
        const subscribe = subscribeToEvents();
        const unsubscribers = [
            subscribe(
                { verb: 'send', namespace: 'deviceManagement', relation: 'readerTraffic', status: 'success' },
                message => {
                    if (
                        message.observationId !== observationRef.current?.id ||
                        String(message.deviceId) !== String(deviceId) ||
                        String(message.tenantId) !== String(tenantId)
                    )
                        return;
                    append([message.event]);
                }
            ),
            subscribe({ verb: 'update', namespace: 'application', relation: 'socket' }, message => {
                if (!current.active) return;
                setNotice(
                    message.isOnline
                        ? 'Connection restored. Recent history reloaded; some live messages may have been missed.'
                        : 'Live connection interrupted. Messages may be missed until it reconnects.'
                );
                if (message.isOnline && current.pageIndex === 0) load(null, 0, true);
            })
        ];
        return () => {
            current.active = false;
            clearTimeout(current.captureTimer);
            unsubscribers.forEach(unsubscribe => unsubscribe());
            const previous = observationRef.current;
            observationRef.current = null;
            if (previous) api.remove(`${base}/observations/${previous.id}`).catch(() => {});
        };
    }, [api, base, scope, visible, deviceId, tenantId, subscribeToEvents]);

    // The server sets the expiry; only schedule a UI update while observing.
    useEffect(() => {
        if (!observation) return;
        const timer = setTimeout(
            () => {
                observationRef.current = null;
                setObservation(null);
                setNotice('Observation expired. Start another observation to see new traffic.');
            },
            Math.max(0, new Date(observation.expiresAt).getTime() - Date.now())
        );
        return () => clearTimeout(timer);
    }, [observation]);

    const toggleObservation = useCallback(
        async (includeTags = false, stop = false) => {
            const current = lifecycle.current;
            if (!current?.active || busy) return;
            setBusy(true);
            setError('');
            try {
                const previous = observationRef.current;
                if (stop) {
                    if (!previous) return;
                    await api.remove(`${base}/observations/${previous.id}`);
                    if (current.active) {
                        observationRef.current = null;
                        setObservation(null);
                        setNotice('Observation stopped. Saved messages remain available.');
                    }
                } else {
                    if (previous) {
                        setError('Observation is already running. Stop it before starting another.');
                        return;
                    }
                    const started = await api.post(`${base}/observations`, { includeTags });
                    // A slow response can arrive after the reader/tab changed. Stop that
                    // session immediately instead of attaching it to the next reader.
                    if (!current.active) {
                        await api.remove(`${base}/observations/${started.id}`);
                        return;
                    }
                    current.load(null, 0, true);
                    observationRef.current = started;
                    setObservation(started);
                    setNotice(
                        `Observing until ${new Date(started.expiresAt).toLocaleTimeString()}. Published commands are not reader acknowledgements.`
                    );
                }
            } catch (err) {
                if (current.active) setError(err.message || 'Unable to change observation');
            } finally {
                if (current.active) setBusy(false);
            }
        },
        [api, base, busy]
    );

    useEffect(() => {
        if (!subscribe || !visible) return;
        const unsubscribeTraffic = subscribe(
            { verb: 'save', namespace: 'deviceManagement', relation: 'device' },
            (payload, context) => {
                const selected = payload.activeRecord?.record || payload.record;
                if (String(selected?._id) !== String(record._id)) return;
                if (context.type === 'traffic.observe') toggleObservation();
                if (context.type === 'traffic.tags') toggleObservation(true);
                if (context.type === 'traffic.stop') toggleObservation(false, true);
            }
        );
        const unsubscribeActions = subscribe(
            { verb: 'mark', namespace: 'deviceManagement', relation: 'device', status: 'success' },
            (payload, context) => {
                if (
                    context.type === 'reader.capture' &&
                    String(payload.activeRecord?.record?._id) === String(record._id)
                )
                    lifecycle.current?.loadCapture();
            }
        );
        return () => {
            unsubscribeTraffic();
            unsubscribeActions();
        };
    }, [subscribe, visible, record._id, toggleObservation]);
    return children({
        records: [
            ...captureCards(capture),
            ...data.messages
                .slice()
                .sort((a, b) => new Date(b.at) - new Date(a.at))
                .map(eventCard)
        ],
        viewReady: !page.loading,
        pagination: {
            label: `Page ${page.index + 1}`,
            loading: page.loading,
            previous:
                page.index > 0 ? () => lifecycle.current.load(page.cursors[page.index - 1], page.index - 1) : null,
            next: page.next ? () => lifecycle.current.load(page.next, page.index + 1) : null
        },
        error,
        status: `${observation ? 'Live observation' : 'Recent messages'} · ${data.messages.length} messages${data.discarded ? ` · ${data.discarded} older messages discarded` : ''}. ${notice}${observation && page.index > 0 ? ' New live messages appear on page 1.' : ''}`
    });
}
